/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import { applyPageFilters } from "@/lib/utility/data/apply-page-filters";
import { PaginationFilterListElement } from "@/lib/utility/data/page-filter-list-element.interface";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { Action } from "vuex-module-decorators";
import { ILocalDataAccessLayer } from "../lib/utility/data/local-data-access-layer.interface";
import { IPageable } from "../lib/utility/data/pagable.interface";
import { IPageDataProvider } from "../lib/utility/data/page-data-provider.interface";
import { IPaginationParams } from "../lib/utility/data/pagination-params.interface";
import { PageDataHandler } from "./../lib/utility/data/page-data-handler";
import { BaseStore } from "./base.store";

/**
 * A scoped, throw-away view onto a store's shared entity cache.
 *
 * Where `BaseStore`/`PaginatedBaseStore` keep a single, module-wide `filters` +
 * pagination cursor (a Vuex singleton — the last consumer to call `setFilters`
 * wins, regardless of which view is on screen), a `FilteredPager` bundles:
 *
 * 1. its OWN mutable filter list, and
 * 2. its OWN `PageDataHandler` + freshly-constructed `IPageDataProvider` (so the
 *    pagination cursor is never shared with any other consumer),
 *
 * while still reading from — and fetching into — the store's shared, append-only
 * entity cache (`ILocalDataAccessLayer`). Two consumers that each hold their own
 * `FilteredPager` can therefore be open concurrently (e.g. a report detail view and
 * a ticket side-card) without their filters or cursors colliding; the only thing
 * they share is the id-keyed entity cache, which is merged (never destructively
 * overwritten) and so is safe to share.
 *
 * Create one via {@link PaginatedBaseStore.createPagerForFilter}, hold it on the
 * consuming component, and discard it when the view closes.
 *
 * Naming mirrors existing conventions on purpose:
 * - `filtered`/`filteredAndSorted`/`setFilters`/`setHiddenFilters` match `IFilterable`.
 * - `currentPage`/`totalPages`/`totalItems`/`itemsPerPage`/`pageSizes`/`isPageLoading`
 *   formally implement `IPageable` (the same interface `PageDataHandler` implements),
 *   so those are trivial delegations.
 * - `paginationList`/`setFilter` (singular) are deprecated aliases kept only for
 *   drop-in compatibility with `TheLayoutPortalList`'s `getModule` contract.
 */
export class FilteredPager<T extends object, Q extends IPaginationParams> implements IPageable {
  /**
   * The query passed to the last `fetchFirstPage`, remembered so `fetchNextPage`
   * can be called without repeating fixed params (e.g. `partnerId`).
   */
  private baseQuery: Q | undefined;

  /**
   * The visible filters scoping this pager. Mutating these only ever affects this
   * pager instance — never the store singleton or any other pager.
   */
  filters: IPageFilterElement[];

  /**
   * Hidden filters (e.g. to hide soft-deleted entities). Applied on top of `filters`.
   */
  hiddenFilters: IPageFilterElement[] = [];

  /**
   * Filter options exposed to the filter card UI. Populated by `createPagerForFilter`
   * from the originating store's `filterOptions`, so the same filter fields are
   * available when the pager is passed directly to `PaginatedTable`.
   */
  filterOptions: PaginationFilterListElement[] = [];

  /**
   * Free-text search string forwarded to the backend on the next `fetchFirstPage` call.
   * Mirrors the same property on `PaginatedBaseStore` so `PaginatedTable` can bind to it.
   */
  search = "";

  constructor(
    private handler: PageDataHandler<T, Q>,
    private data: ILocalDataAccessLayer<T>,
    filters: IPageFilterElement[]
  ) {
    this.filters = filters;
    // Bind so that debounce(pager.fetchFirstPage) keeps the correct `this`
    // when the method reference is extracted (e.g. in PaginatedTable).
    this.fetchFirstPage = this.fetchFirstPage.bind(this);
    this.fetchNextPage = this.fetchNextPage.bind(this);
  }

  setFilters(filters: IPageFilterElement[]): this {
    this.filters = filters;
    return this;
  }

  setHiddenFilters(hiddenFilters: IPageFilterElement[]): this {
    this.hiddenFilters = hiddenFilters;
    return this;
  }

  /** @deprecated alias for setFilters — TheLayoutPortalList's getModule contract calls this */
  setFilter(filters: IPageFilterElement[]): this {
    return this.setFilters(filters);
  }

  /** Reactive: re-derived from the shared cache on every access, scoped to this pager's filters. */
  get filtered(): T[] {
    return applyPageFilters(this.data.entities, [...this.filters, ...this.hiddenFilters]);
  }

  get filteredAndSorted(): T[] {
    return this.filtered.slice().sort((a, b) => (this.data.getIdentifier(a) > this.data.getIdentifier(b) ? -1 : 1));
  }

  /** @deprecated alias for filteredAndSorted — TheLayoutPortalList's getModule contract reads this */
  get paginationList(): T[] {
    return this.filteredAndSorted;
  }

  get isPageLoading() {
    return this.handler.isPageLoading;
  }

  /** Alias for `isPageLoading` — matches `PaginatedBaseStore`'s interface for use with `PaginatedTable`. */
  get isLoading() {
    return this.isPageLoading;
  }

  get currentPage() {
    return this.handler.currentPage;
  }

  get totalPages() {
    return this.handler.totalPages;
  }

  get totalItems() {
    return this.handler.totalItems;
  }

  get itemsPerPage() {
    return this.handler.itemsPerPage;
  }

  /** Sets the free-text search string. */
  setSearch(search: string): this {
    this.search = search;
    return this;
  }

  /** Delegates to the underlying `PageDataHandler`. */
  setItemsPerPage(n: number): this {
    this.handler.itemsPerPage = n;
    return this;
  }

  get pageSizes() {
    return this.handler.pageSizes;
  }

  get hasMore() {
    return this.currentPage < this.totalPages;
  }

  private withFilter(query: Q): Q {
    // Mirror the ordering the store's own fetch methods (`PaginatedBaseStore.fetch*`)
    // always send: the backend defaults to a different order when `order` is omitted,
    // and the whole pagination system (incl. startId cursoring) assumes newest-first.
    // Dropping it would make a source with more than one page cache the wrong slice.
    return {
      ...query,
      order: query.order ?? PageOrderEnum.DESCENDING,
      filter: [...this.filters, ...this.hiddenFilters].map((f) => f.dtoPagination)
    } as Q;
  }

  async fetchFirstPage(query: Q) {
    this.baseQuery = query;
    const q: Q = this.search ? ({ ...query, search: this.search } as Q) : query;
    return this.handler.fetchFirstPage(this.withFilter(q));
  }

  /**
   * The number of entities the backend holds for this pager's filters, read from the page meta of a
   * single-item first page instead of loading every entity. Restarts the pager's cursor.
   */
  async count(query: Q): Promise<number> {
    const itemsPerPage = this.itemsPerPage;
    this.setItemsPerPage(1);

    try {
      await this.fetchFirstPage(query);
    } finally {
      this.setItemsPerPage(itemsPerPage);
    }

    return this.totalItems;
  }

  async fetchNextPage(query?: Q) {
    if (!this.baseQuery && !query) {
      throw new Error(
        "FilteredPager.fetchNextPage requires a query, or a prior fetchFirstPage(query) whose query it can reuse."
      );
    }

    const merged = { ...(this.baseQuery ?? {}), ...(query ?? {}) } as Q;
    return this.handler.fetchNextPage(this.withFilter(merged));
  }
}

/**
 * Adds pagination to the store
 */
export abstract class PaginatedBaseStore<T extends object, Q extends IPaginationParams> extends BaseStore<T> {
  /**
   * The page provider
   */
  protected abstract _pageProvider: IPageDataProvider<T, Q>;

  /**
   * The pager for executing page requests
   */
  protected abstract _pager: PageDataHandler<T, Q>;

  /**
   * @inheritdoc
   */
  get isLoading() {
    return this._pager.isPageLoading;
  }

  /**
   * @inheritdoc
   */
  get itemsPerPage() {
    return this._pager.itemsPerPage;
  }

  @Action
  setItemsPerPage(itemsPerPage: number) {
    this._pager.itemsPerPage = itemsPerPage;
  }

  /**
   * @inheritdoc
   */
  get pageSizes() {
    return this._pager.pageSizes;
  }

  /**
   * @inheritdoc
   */
  get currentPage() {
    return this._pager.currentPage;
  }

  /**
   * @inheritdoc
   */
  get totalPages() {
    return this._pager.totalPages;
  }

  /**
   * @inheritdoc
   */
  get totalItems() {
    return this._pager.totalItems;
  }

  /**
   * @returns the filtered entities
   * @deprecated use filtered. Backwards compatible getter for `TheLayoutPaginated`.
   */
  get paginationList() {
    return this.filteredAndSorted;
  }

  /**
   * Creates a {@link FilteredPager} scoped to the given filters, so a consumer can own its
   * own filter + pagination cursor instead of sharing this module's singleton state.
   *
   * The pager reads from — and fetches into — this store's shared, append-only entity cache
   * (`this._data`), but gets a brand-new `PageDataHandler` backed by a freshly minted
   * `IPageDataProvider` instance, so its cursor is independent from the module and from every
   * other pager. Use this when the same store is consumed by two views that can be open at the
   * same time (e.g. a report detail view and a ticket side-card) to stop their filters/cursors
   * from clobbering each other.
   *
   * `this._pageProvider.constructor` points at the (anonymous) provider class exported by the
   * page-data-provider files, so `new ProviderCtor()` mints a fresh, independent instance with
   * its own private cursor fields — no changes are needed to those provider files.
   *
   * NOTE: this is a Vuex `@Action`, so it is dispatched and resolves asynchronously —
   * the return type is `Promise<FilteredPager<T, Q>>`; `await` the result.
   */
  @Action
  async createPagerForFilter(filters: IPageFilterElement[]): Promise<FilteredPager<T, Q>> {
    const ProviderCtor = this._pageProvider.constructor as new () => IPageDataProvider<T, Q>;
    const handler = new PageDataHandler(this._data, new ProviderCtor());

    const pager = new FilteredPager(handler, this._data, filters);
    pager.filterOptions = [...(this.filterOptions ?? [])];
    return pager;
  }

  /**
   * The number of entities the backend holds for the given filters, e.g. for a badge. Counts through its own
   * {@link FilteredPager}, so the module's filters and pagination cursor stay untouched.
   */
  @Action
  async countForFilter({ filters, query }: { filters: IPageFilterElement[]; query: Q }): Promise<number> {
    const pager: FilteredPager<T, Q> = await this.context.dispatch("createPagerForFilter", filters);

    return pager.count(query);
  }

  @Action
  async fetchPage(query: Q): Promise<T[] | undefined> {
    return await this._pager.fetchPage({
      ...query,
      filter: [...this.filters, ...this.hiddenFilter].map((d) => d.dtoPagination),
      search: this.search,
      order: PageOrderEnum.DESCENDING,
      itemsPerPage: this.itemsPerPage
    });
  }

  @Action
  async fetchFirstPage(query: Q & { itemsPerPage?: number }): Promise<T[] | undefined> {
    return await this._pager.fetchFirstPage({
      ...query,
      filter: [...this.filters, ...this.hiddenFilter].map((d) => d.dtoPagination),
      search: this.search,
      order: PageOrderEnum.DESCENDING,
      itemsPerPage: query.itemsPerPage ?? this.itemsPerPage
    });
  }

  @Action
  async fetchNextPage(query: Q): Promise<T[] | undefined> {
    return await this._pager.fetchNextPage({
      ...query,
      filter: [...this.filters, ...this.hiddenFilter].map((d) => d.dtoPagination),
      search: this.search,
      order: PageOrderEnum.DESCENDING,
      itemsPerPage: this.itemsPerPage
    });
  }

  /**
   * @deprecated
   */
  @Action
  async fetchAll(query: Q) {
    const itemsPerPage = this._pageProvider.itemsPerPage;
    this._pageProvider.itemsPerPage = 500;
    const res = await this.context.dispatch("fetchFirstPage", query);

    if (res.length) {
      await this.context.dispatch("_fetchAllRest", query).then(() => (this._pageProvider.itemsPerPage = itemsPerPage));
    }
  }

  /**
   * @deprecated
   */
  @Action
  async fetchAllFromBeginning(query: IPaginationParams) {
    this.context.dispatch("fetchAll", query);
  }

  /**
   * @deprecated
   */
  @Action
  private async _fetchAllRest(query: Q) {
    await this.context.dispatch("fetchRemaining", query);
  }

  /**
   * @deprecated
   */
  @Action
  async fetchRemaining(query: Q) {
    const previousPage = this._pageProvider.currentPage;

    if (this._pageProvider.totalPages <= previousPage) {
      return;
    }

    const res = await this.context.dispatch("fetchNextPage", query);

    if (res.length) {
      await this.context.dispatch("fetchRemaining", query);
    }
  }
}
